import React, { useState, useEffect } from "react";
import Modal from "../modal/Modal";
import { Typography, Box, CircularProgress, Grid, Chip, Alert, Paper } from "@mui/material";
import {
    PhotoOutlined,
    VideoFileOutlined,
    PictureAsPdfOutlined,
    InsertDriveFileOutlined,
    GifBoxOutlined,
    WarningAmberOutlined,
} from "@mui/icons-material";
import Button from "../ui/button/Button";
import { useDispatch } from "react-redux";
import {
    MediaType,
    MAX_FILE_SIZES,
    MIME_TYPE_MAPPINGS,
    FILE_EXTENSIONS,
} from "../../constants/mediaConstants";
import {
    getMediaType,
    validateFileSize,
    validateFileType,
    formatFileSize,
    getFileExtension,
} from "../../utils/mediaUtils";
import apiEndpointManager from "../../config/index";

/**
 * Enhanced FileUploadModal Props
 *
 * Supports multiple media types with comprehensive validation and preview
 */
interface FileUploadModalProps {
    isVisible: boolean;
    onClose: () => void;
    fileType: MediaType | string; // Support both new MediaType and legacy string
    onFilesUploaded?: (
        filesData: Array<{
            mediaType: string;
            mediaUrl: string;
            thumbnailUrl?: string;
            fileName?: string;
            fileSize?: number;
            mimeType?: string;
        }>,
    ) => void;
    maxFiles?: number; // Maximum number of files allowed
    allowMultiple?: boolean; // Whether to allow multiple file selection
}

/**
 * File with metadata for internal processing
 */
interface FileWithMetadata {
    file: File;
    mediaType: MediaType;
    preview?: string;
    error?: string;
    isValid: boolean;
}

/**
 * Enhanced FileUploadModal Component
 *
 * This component provides a comprehensive file upload interface supporting:
 * - Multiple media types (images, videos, PDFs, documents, GIFs)
 * - Proper file validation with size and type checking
 * - Preview generation for different file types
 * - Drag and drop support (future enhancement)
 * - Progress tracking for large uploads
 * - Detailed error messaging and validation feedback
 */
const FileUploadModal: React.FC<FileUploadModalProps> = ({
    isVisible,
    onClose,
    fileType,
    onFilesUploaded,
    maxFiles = 10,
    allowMultiple = true,
}) => {
    // State management for file handling
    const [filesWithMetadata, setFilesWithMetadata] = useState<FileWithMetadata[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const dispatch = useDispatch();

    // Advanced drag-and-drop state
    const [isDragActive, setIsDragActive] = useState(false);
    const [dragPreviewFiles, setDragPreviewFiles] = useState<File[]>([]);
    const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
    const [isDragPreviewVisible, setIsDragPreviewVisible] = useState(false);
    const [dragValidationStatus, setDragValidationStatus] = useState<
        "valid" | "invalid" | "mixed" | null
    >(null);

    // Determine the target media type (handle legacy string types)
    const targetMediaType: MediaType =
        fileType === "file" || fileType === "document" ? "document" : (fileType as MediaType);

    /**
     * Reset component state when modal visibility changes
     * This ensures clean state on each modal open/close cycle
     */
    useEffect(() => {
        if (isVisible) {
            document.body.classList.add("no-scroll");
        } else {
            document.body.classList.remove("no-scroll");
            // Complete state reset when modal closes
            setFilesWithMetadata([]);
            setUploadError(null);
            setValidationErrors([]);
        }

        return () => document.body.classList.remove("no-scroll");
    }, [isVisible]);

    /**
     * Comprehensive file validation function
     *
     * Validates each file against:
     * - Media type compatibility
     * - File size limits
     * - MIME type restrictions
     * - File extension validation
     */
    const validateFile = (
        file: File,
    ): { isValid: boolean; error?: string; mediaType?: MediaType } => {
        // Determine media type from file
        const detectedMediaType = getMediaType(file);

        if (!detectedMediaType) {
            return {
                isValid: false,
                error: `Unsupported file type: ${file.type || "unknown"}`,
            };
        }

        // Check if file type matches expected type (with flexibility for legacy)
        if (
            targetMediaType !== detectedMediaType &&
            !(targetMediaType === "document" && detectedMediaType === "pdf")
        ) {
            return {
                isValid: false,
                error: `Expected ${targetMediaType} file, but got ${detectedMediaType} file`,
                mediaType: detectedMediaType,
            };
        }

        // Validate file size
        if (!validateFileSize(file, detectedMediaType)) {
            const maxSizeMB = (MAX_FILE_SIZES[detectedMediaType] / (1024 * 1024)).toFixed(1);
            const actualSizeMB = (file.size / (1024 * 1024)).toFixed(2);

            return {
                isValid: false,
                error: `File size ${actualSizeMB}MB exceeds maximum ${maxSizeMB}MB for ${detectedMediaType} files`,
                mediaType: detectedMediaType,
            };
        }

        // Validate file type (MIME type and extension)
        if (!validateFileType(file, detectedMediaType)) {
            return {
                isValid: false,
                error: `Invalid ${detectedMediaType} file format`,
                mediaType: detectedMediaType,
            };
        }

        return {
            isValid: true,
            mediaType: detectedMediaType,
        };
    };

    /**
     * Generate preview for different file types
     *
     * Creates appropriate previews based on media type:
     * - Images: Thumbnail image
     * - Videos: Video element (may show first frame)
     * - PDFs: PDF icon with file info
     * - Documents: Document icon with file info
     */
    const generatePreview = (file: File, mediaType: MediaType): Promise<string | undefined> => {
        return new Promise((resolve) => {
            if (mediaType === "image" || mediaType === "gif") {
                // Generate image thumbnail
                const reader = new FileReader();
                reader.onload = (event) => {
                    resolve(event.target?.result as string);
                };
                reader.onerror = () => resolve(undefined);
                reader.readAsDataURL(file);
            } else if (mediaType === "video") {
                // For videos, we could generate a thumbnail from the first frame
                // For now, we'll use the video file directly for preview
                const reader = new FileReader();
                reader.onload = (event) => {
                    resolve(event.target?.result as string);
                };
                reader.onerror = () => resolve(undefined);
                reader.readAsDataURL(file);
            } else {
                // For PDFs and documents, no preview needed (will show icon)
                resolve(undefined);
            }
        });
    };

    /**
     * Handle file selection with comprehensive validation
     *
     * Processes selected files through validation pipeline and generates previews
     */
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        if (!target.files || target.files.length === 0) return;

        const selectedFiles = Array.from(target.files);

        // Reset previous errors
        setUploadError(null);
        setValidationErrors([]);

        // Check file count limits
        if (!allowMultiple && selectedFiles.length > 1) {
            setUploadError(`Only one ${targetMediaType} file is allowed`);
            return;
        }

        if (selectedFiles.length > maxFiles) {
            setUploadError(`Maximum ${maxFiles} files allowed`);
            return;
        }

        // Process each file with validation and preview generation
        const processedFiles: FileWithMetadata[] = [];
        const errors: string[] = [];

        for (const file of selectedFiles) {
            const validation = validateFile(file);

            if (validation.isValid && validation.mediaType) {
                // Generate preview for valid files
                const preview = await generatePreview(file, validation.mediaType);

                processedFiles.push({
                    file,
                    mediaType: validation.mediaType,
                    preview,
                    isValid: true,
                });
            } else {
                // Collect validation errors
                processedFiles.push({
                    file,
                    mediaType: targetMediaType, // Default for display
                    error: validation.error,
                    isValid: false,
                });

                if (validation.error) {
                    errors.push(`${file.name}: ${validation.error}`);
                }
            }
        }

        setFilesWithMetadata(processedFiles);
        setValidationErrors(errors);

        // Clear the input to allow selecting the same files again
        target.value = "";
    };

    /**
     * Advanced drag-and-drop functionality with live preview
     */

    // Handle drag enter event - file enters drag zone
    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    };

    // Handle drag over event - file is being dragged over zone
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        // Update drag position for preview positioning
        setDragPosition({ x: e.clientX, y: e.clientY });

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const files = Array.from(e.dataTransfer.files);

            // Show preview of dragged files
            if (!isDragPreviewVisible) {
                setDragPreviewFiles(files);
                setIsDragPreviewVisible(true);

                // Validate files to show appropriate visual feedback
                const validation = validateDraggedFiles(files);
                setDragValidationStatus(validation);
            }
        }
    };

    // Handle drag leave event - file leaves drag zone
    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        // Only hide if leaving the main drop zone (not child elements)
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;

        if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            setIsDragActive(false);
            setIsDragPreviewVisible(false);
            setDragPreviewFiles([]);
            setDragValidationStatus(null);
        }
    };

    // Handle file drop event
    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragActive(false);
        setIsDragPreviewVisible(false);
        setDragPreviewFiles([]);
        setDragValidationStatus(null);

        const droppedFiles = Array.from(e.dataTransfer.files);

        if (droppedFiles.length === 0) return;

        // Process dropped files the same way as selected files
        await processDroppedFiles(droppedFiles);
    };

    // Validate files during drag for immediate feedback
    const validateDraggedFiles = (files: File[]): "valid" | "invalid" | "mixed" => {
        let validCount = 0;
        let invalidCount = 0;

        for (const file of files) {
            const validation = validateFile(file);
            if (validation.isValid) {
                validCount++;
            } else {
                invalidCount++;
            }
        }

        if (validCount > 0 && invalidCount === 0) return "valid";
        if (invalidCount > 0 && validCount === 0) return "invalid";
        return "mixed";
    };

    // Process dropped files
    const processDroppedFiles = async (droppedFiles: File[]) => {
        // Reset previous errors
        setUploadError(null);
        setValidationErrors([]);

        // Check file count limits
        if (!allowMultiple && droppedFiles.length > 1) {
            setUploadError(`Only one ${targetMediaType} file is allowed`);
            return;
        }

        if (droppedFiles.length > maxFiles) {
            setUploadError(`Maximum ${maxFiles} files allowed`);
            return;
        }

        // Process each file with validation and preview generation
        const processedFiles: FileWithMetadata[] = [];
        const errors: string[] = [];

        for (const file of droppedFiles) {
            const validation = validateFile(file);

            if (validation.isValid && validation.mediaType) {
                // Generate preview for valid files
                const preview = await generatePreview(file, validation.mediaType);

                processedFiles.push({
                    file,
                    mediaType: validation.mediaType,
                    preview,
                    isValid: true,
                });
            } else {
                // Collect validation errors
                processedFiles.push({
                    file,
                    mediaType: targetMediaType, // Default for display
                    error: validation.error,
                    isValid: false,
                });

                if (validation.error) {
                    errors.push(`${file.name}: ${validation.error}`);
                }
            }
        }

        setFilesWithMetadata(processedFiles);
        setValidationErrors(errors);
    };

    /**
     * Handle file upload process
     *
     * Uploads files to backend and returns the uploaded media URLs
     */
    const handleUpload = async () => {
        const validFiles = filesWithMetadata.filter((fm) => fm.isValid);

        if (validFiles.length === 0) {
            setUploadError("No valid files to upload");
            return;
        }

        setLoading(true);
        setUploadError(null);

        try {
            const uploadedFiles: Array<{
                mediaType: string;
                mediaUrl: string;
                thumbnailUrl?: string;
                fileName: string;
                fileSize: number;
                mimeType: string;
            }> = [];

            // Upload each file to the backend
            for (const fileMetadata of validFiles) {
                const uploadedFile = await uploadFileToBackend(fileMetadata);
                uploadedFiles.push(uploadedFile);
            }

            // Callback to parent component with uploaded file URLs
            onFilesUploaded?.(uploadedFiles);

            setLoading(false);
            onClose();
        } catch (error) {
            // TODO: Make different error responses and handling for different types of files
            console.error("Error uploading files:", error);
            setUploadError(
                error instanceof Error
                    ? error.message
                    : "An error occurred while uploading your files. Please try again.",
            );
            setLoading(false);
        }
    };

    /**
     * Upload a single file to the backend using the uploadMedia API
     */
    const uploadFileToBackend = async (
        fileMetadata: FileWithMetadata,
    ): Promise<{
        mediaType: string;
        mediaUrl: string;
        thumbnailUrl?: string;
        fileName: string;
        fileSize: number;
        mimeType: string;
    }> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = async (event) => {
                if (!event.target?.result) {
                    reject(new Error(`Failed to read file: ${fileMetadata.file.name}`));
                    return;
                }

                try {
                    const base64Data = event.target.result as string;
                    const token = localStorage.getItem("accessToken");

                    if (!token) {
                        reject(new Error("User is not authenticated. Please log in again."));
                        return;
                    }

                    // Get API endpoint
                    const endpoint = await apiEndpointManager.getEndpoint();

                    // Prepare upload payload for the new uploadMedia API
                    const uploadPayload = {
                        image: base64Data, // Backend still expects 'image' field for backward compatibility
                        mediaType: fileMetadata.mediaType, // New field to specify media type
                        imageType: "postPhoto", // Set as postPhoto for post-related media
                        fileName: fileMetadata.file.name,
                        fileSize: fileMetadata.file.size,
                        mimeType: fileMetadata.file.type,
                        // Note: We don't have postId yet for new posts, backend will handle this gracefully
                    };

                    const response = await fetch(`${endpoint}/profileImages/uploadMedia`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(uploadPayload),
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(
                            errorData.message || `Failed to upload ${fileMetadata.file.name}`,
                        );
                    }

                    const result = await response.json();

                    resolve({
                        mediaType: fileMetadata.mediaType,
                        mediaUrl: result.imageUrl || result.mediaUrl, // Backend may return either field name
                        thumbnailUrl: result.thumbnailUrl || fileMetadata.preview,
                        fileName: fileMetadata.file.name,
                        fileSize: fileMetadata.file.size,
                        mimeType: fileMetadata.file.type,
                    });
                } catch (error) {
                    reject(
                        new Error(
                            `Failed to upload ${fileMetadata.file.name}: ${error instanceof Error ? error.message : "Unknown error"}`,
                        ),
                    );
                }
            };

            reader.onerror = () =>
                reject(new Error(`Error reading file: ${fileMetadata.file.name}`));
            reader.readAsDataURL(fileMetadata.file);
        });
    };

    /**
     * Get accepted file types for HTML input element
     * Based on target media type and supported MIME types
     */
    const getAcceptedFileTypes = (): string => {
        // Safety check for undefined or invalid media types
        if (
            !targetMediaType ||
            !MIME_TYPE_MAPPINGS[targetMediaType] ||
            !FILE_EXTENSIONS[targetMediaType]
        ) {
            return ""; // Return empty string to prevent crash
        }

        const mimeTypes = MIME_TYPE_MAPPINGS[targetMediaType];
        const extensions = FILE_EXTENSIONS[targetMediaType];

        // Combine MIME types and extensions for maximum compatibility
        return [...mimeTypes, ...extensions].join(",");
    };

    /**
     * Get display name for media type
     */
    const getMediaTypeDisplayName = (): string => {
        const names = {
            image: "Images",
            video: "Videos",
            pdf: "PDF Documents",
            gif: "GIF Animations",
            document: "Documents",
        };
        return names[targetMediaType] || "Files";
    };

    /**
     * Get icon for media type
     */
    const getMediaTypeIcon = (mediaType: MediaType) => {
        const icons = {
            image: <PhotoOutlined />,
            video: <VideoFileOutlined />,
            pdf: <PictureAsPdfOutlined />,
            gif: <GifBoxOutlined />,
            document: <InsertDriveFileOutlined />,
        };
        return icons[mediaType] || <InsertDriveFileOutlined />;
    };

    /**
     * Get supported formats description
     */
    const getSupportedFormatsDescription = (): string => {
        const extensions = FILE_EXTENSIONS[targetMediaType];
        const maxSize = MAX_FILE_SIZES[targetMediaType];

        // Safety check for undefined values
        if (!extensions || !maxSize) {
            // console.warn(
            //     `Unknown media type: ${targetMediaType}. Available types:`,
            //     Object.keys(FILE_EXTENSIONS),
            // );
            return `File type: ${targetMediaType} • Configure file extensions and size limits`;
        }

        const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0);
        return `Supported: ${extensions.join(", ")} • Max size: ${maxSizeMB}MB per file`;
    };

    // Calculate valid and invalid file counts
    const validFilesCount = filesWithMetadata.filter((fm) => fm.isValid).length;
    const invalidFilesCount = filesWithMetadata.filter((fm) => !fm.isValid).length;

    return (
        <Modal
            title={`Upload ${getMediaTypeDisplayName()}`}
            isVisible={isVisible}
            onClose={onClose}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 400 }}>
                {/* Media type information header */}
                <Paper elevation={1} sx={{ p: 2, backgroundColor: "background.default" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        {getMediaTypeIcon(targetMediaType)}
                        <Typography variant="h6">{getMediaTypeDisplayName()}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {getSupportedFormatsDescription()}
                    </Typography>
                </Paper>

                {/* Enhanced drag-and-drop file input area */}
                <Box
                    className={isDragActive ? "dragActive" : ""}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                        border: "2px dashed",
                        borderColor: isDragActive
                            ? dragValidationStatus === "valid"
                                ? "success.main"
                                : dragValidationStatus === "invalid"
                                  ? "error.main"
                                  : "warning.main"
                            : "divider",
                        borderRadius: 2,
                        p: 3,
                        textAlign: "center",
                        position: "relative",
                        backgroundColor: isDragActive
                            ? dragValidationStatus === "valid"
                                ? "rgba(46, 125, 50, 0.1)"
                                : dragValidationStatus === "invalid"
                                  ? "rgba(211, 47, 47, 0.1)"
                                  : "rgba(237, 108, 2, 0.1)"
                            : "transparent",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        transform: isDragActive ? "scale(1.02)" : "scale(1)",
                        "&:hover": {
                            borderColor: "primary.main",
                            backgroundColor: "rgba(25, 118, 210, 0.05)",
                        },
                    }}
                >
                    <input
                        type="file"
                        accept={getAcceptedFileTypes()}
                        multiple={allowMultiple && targetMediaType === "image"} // Only allow multiple for images
                        onChange={handleFileChange}
                        disabled={loading}
                        style={{
                            width: "100%",
                            padding: "0.5rem",
                            border: "none",
                            backgroundColor: "transparent",
                            cursor: loading ? "not-allowed" : "pointer",
                        }}
                    />

                    {/* Dynamic text based on drag state */}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {isDragActive
                            ? dragValidationStatus === "valid"
                                ? `Drop ${dragPreviewFiles.length} valid file${dragPreviewFiles.length !== 1 ? "s" : ""} here`
                                : dragValidationStatus === "invalid"
                                  ? `Cannot drop these file${dragPreviewFiles.length !== 1 ? "s" : ""}`
                                  : `Some files are invalid`
                            : "Click to browse or drag and drop files here"}
                    </Typography>

                    {/* Enhanced drag state indicator */}
                    {isDragActive && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor:
                                    dragValidationStatus === "valid"
                                        ? "rgba(46, 125, 50, 0.2)"
                                        : dragValidationStatus === "invalid"
                                          ? "rgba(211, 47, 47, 0.2)"
                                          : "rgba(237, 108, 2, 0.2)",
                                borderRadius: 2,
                                pointerEvents: "none",
                                animation: "pulse 1.5s ease-in-out infinite",
                            }}
                        >
                            <Typography
                                variant="h6"
                                color={
                                    dragValidationStatus === "valid"
                                        ? "success.main"
                                        : dragValidationStatus === "invalid"
                                          ? "error.main"
                                          : "warning.main"
                                }
                                sx={{ fontWeight: "bold" }}
                            >
                                {dragValidationStatus === "valid"
                                    ? "✓ Drop to Upload"
                                    : dragValidationStatus === "invalid"
                                      ? "✗ Invalid Files"
                                      : "⚠ Some Invalid"}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Advanced drag preview component */}
                {isDragPreviewVisible && dragPreviewFiles.length > 0 && (
                    <Box
                        sx={{
                            position: "fixed",
                            top: dragPosition.y - 50,
                            left: dragPosition.x - 100,
                            zIndex: 9999,
                            pointerEvents: "none",
                            backgroundColor: "background.paper",
                            border: "2px solid",
                            borderColor:
                                dragValidationStatus === "valid"
                                    ? "success.main"
                                    : dragValidationStatus === "invalid"
                                      ? "error.main"
                                      : "warning.main",
                            borderRadius: 2,
                            p: 1,
                            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                            backdropFilter: "blur(8px)",
                            animation: "fadeIn 0.2s ease-out",
                            maxWidth: 200,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: "bold",
                                color:
                                    dragValidationStatus === "valid"
                                        ? "success.main"
                                        : dragValidationStatus === "invalid"
                                          ? "error.main"
                                          : "warning.main",
                            }}
                        >
                            {dragPreviewFiles.length} file{dragPreviewFiles.length !== 1 ? "s" : ""}
                        </Typography>

                        {/* Show preview thumbnails for up to 3 files */}
                        <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                            {dragPreviewFiles.slice(0, 3).map((file, index) => {
                                const mediaType = getMediaType(file);
                                const isValid = validateFile(file).isValid;

                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: 1,
                                            backgroundColor: isValid
                                                ? "success.light"
                                                : "error.light",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            border: "1px solid",
                                            borderColor: isValid ? "success.main" : "error.main",
                                        }}
                                    >
                                        {mediaType && (
                                            <Box sx={{ fontSize: 12 }}>
                                                {getMediaTypeIcon(mediaType)}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}

                            {/* Show count if more than 3 files */}
                            {dragPreviewFiles.length > 3 && (
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: 1,
                                        backgroundColor: "action.hover",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontSize: 10 }}>
                                        +{dragPreviewFiles.length - 3}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Validation errors display */}
                {validationErrors.length > 0 && (
                    <Alert severity="warning" icon={<WarningAmberOutlined />}>
                        <Typography variant="subtitle2" gutterBottom>
                            File Validation Issues:
                        </Typography>
                        {validationErrors.map((error, index) => (
                            <Typography key={index} variant="body2" sx={{ ml: 1 }}>
                                • {error}
                            </Typography>
                        ))}
                    </Alert>
                )}

                {/* Upload error display */}
                {uploadError && <Alert severity="error">{uploadError}</Alert>}

                {/* File preview section */}
                {filesWithMetadata.length > 0 && (
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            Selected Files ({validFilesCount} valid, {invalidFilesCount} invalid)
                        </Typography>

                        <Grid container spacing={2}>
                            {filesWithMetadata.map((fileMetadata, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Paper
                                        elevation={2}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            border: fileMetadata.isValid
                                                ? "1px solid green"
                                                : "1px solid red",
                                            backgroundColor: fileMetadata.isValid
                                                ? "success.light"
                                                : "error.light",
                                            opacity: fileMetadata.isValid ? 1 : 0.7,
                                        }}
                                    >
                                        {/* File preview */}
                                        {fileMetadata.preview &&
                                            (fileMetadata.mediaType === "image" ||
                                                fileMetadata.mediaType === "gif") && (
                                                <Box sx={{ mb: 1 }}>
                                                    <img
                                                        src={fileMetadata.preview}
                                                        alt={fileMetadata.file.name}
                                                        style={{
                                                            width: "100%",
                                                            height: "80px",
                                                            objectFit: "cover",
                                                            borderRadius: "4px",
                                                        }}
                                                    />
                                                </Box>
                                            )}

                                        {fileMetadata.preview &&
                                            fileMetadata.mediaType === "video" && (
                                                <Box sx={{ mb: 1 }}>
                                                    <video
                                                        src={fileMetadata.preview}
                                                        style={{
                                                            width: "100%",
                                                            height: "80px",
                                                            objectFit: "cover",
                                                            borderRadius: "4px",
                                                        }}
                                                        muted
                                                    />
                                                </Box>
                                            )}

                                        {(!fileMetadata.preview ||
                                            (fileMetadata.mediaType !== "image" &&
                                                fileMetadata.mediaType !== "gif" &&
                                                fileMetadata.mediaType !== "video")) && (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    mb: 1,
                                                }}
                                            >
                                                {getMediaTypeIcon(fileMetadata.mediaType)}
                                            </Box>
                                        )}

                                        {/* File information */}
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            title={fileMetadata.file.name}
                                        >
                                            {fileMetadata.file.name}
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                mt: 1,
                                            }}
                                        >
                                            <Chip
                                                label={fileMetadata.mediaType}
                                                size="small"
                                                color={fileMetadata.isValid ? "success" : "error"}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                {formatFileSize(fileMetadata.file.size)}
                                            </Typography>
                                        </Box>

                                        {/* Error message for invalid files */}
                                        {!fileMetadata.isValid && fileMetadata.error && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                                sx={{ mt: 1, display: "block" }}
                                            >
                                                {fileMetadata.error}
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* Upload controls */}
                <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                    <Button
                        onClick={onClose}
                        disabled={loading}
                        label="Cancel"
                        variant="secondary"
                    />

                    <Button
                        onClick={handleUpload}
                        disabled={loading || validFilesCount === 0}
                        label={
                            loading
                                ? "Processing..."
                                : `Upload ${validFilesCount} File${validFilesCount !== 1 ? "s" : ""}`
                        }
                        variant="primary"
                    />
                </Box>

                {/* Loading indicator */}
                {loading && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 2,
                            mt: 2,
                        }}
                    >
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                            Processing files for upload...
                        </Typography>
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default FileUploadModal;
